import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Cache the callback promise at module level so React StrictMode double-mount
// doesn't consume the OIDC state twice (same pattern as handoff in useAuth).
let _callbackPromise: Promise<void> | null = null;

export function OktaCallbackPage() {
  // `tenant` is present only on the legacy slug-routed callback
  // (`/t/:tenant/auth/okta/callback`). The canonical static callback
  // (`/auth/okta/callback`) has no slug — tenant resolves from state on the
  // server.
  const { tenant } = useParams<{ tenant?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithOktaCallback } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code || !state) {
      setError('Missing authentication parameters');
      return;
    }

    if (!_callbackPromise) {
      _callbackPromise = loginWithOktaCallback({ code, state, tenantSlug: tenant });
    }

    _callbackPromise
      .then(() => navigate('/', { replace: true }))
      .catch((err) => setError(err instanceof Error ? err.message : 'Authentication failed'));
  }, []);

  if (error) {
    return (
      <div className="login-container">
        <div className="login-card">
          <span className="login-logo-text">Cade</span>
          <p className="login-error">{error}</p>
          <button className="btn-primary" onClick={() => navigate('/login', { replace: true })}>
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card" style={{ alignItems: 'center', gap: 12 }}>
        <span className="login-logo-text">Cade</span>
        <span style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>Signing in with Okta...</span>
      </div>
    </div>
  );
}
